import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CardGrid, CtyButton, CtyShowToast, EntityCardWithActions, checkType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { AGENT_BUILDER_URL, agentBuilderApi, useAgentBuilderAuth, type AgentSummary, type Blueprint } from './agentBuilderApi';
import { AIWorkerList } from '../lazyload/lazyload';

// ASJs live in the Journeys section: listed on the Journeys page and built at JOURNEYS_PATH/asj/<name>.
export const JOURNEYS_PATH = '/aiworkers/list/journeys';
export const ASJ_PATH = `${JOURNEYS_PATH}/asj`;
const DEFAULT_MODEL = 'default';

// The journeys (ASJs) built by chat, as a section of the Journeys page, and the "Create ASJ"
// dialog: a name, a model and the blueprint it is built in. The type (ASJ) is implied.
// basePath: where an ASJ opens (the Journeys area, or the temporary preview route).
export function AsjJourneys({
  basePath = ASJ_PATH,
  isCreateOpen,
  onCreateOpenChange,
  onCount,
}: {
  basePath?: string;
  isCreateOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
  onCount?: (count: number) => void; // how many ASJs are listed (the page hides its empty message)
}) {
  useAgentBuilderAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = () => agentBuilderApi.agents().then(setAgents, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    agentBuilderApi.blueprints().then(setBlueprints, () => undefined);
  }, []);
  useEffect(() => {
    if (agents) onCount?.(agents.length);
  }, [agents, onCount]);

  const remove = async (name: string, close: () => void) => {
    setDeleting(name);
    try {
      await agentBuilderApi.deleteAgent(name);
      CtyShowToast(checkType.success, t('Journey deleted.'));
      close();
      await load();
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setDeleting(null);
    }
  };

  const titleOf = (id: string | null) => blueprints.find((b) => b.id === id)?.title;

  return (
    <section className="px-4 pt-2" aria-label={t('Service journeys')} data-testid="agent-builder-list">
      {error && (
        <p className="text-sm text-danger" title={AGENT_BUILDER_URL}>
          {t('The agent builder is unavailable right now. Please try again in a moment.')}
        </p>
      )}
      {!agents && !error && <p className="text-sm text-default-500">{t('Loading…')}</p>}
      {agents && agents.length === 0 && <p className="text-sm text-default-500">{t('No service journeys yet. Use Create Journey to start one.')}</p>}
      {/* The same cards as the Agents section: name, description, actions menu, open button. */}
      <CardGrid>
        {agents?.map((a) => (
          <div key={a.name} data-testid={`ab-agent-${a.name}`}>
            <EntityCardWithActions
              id={`asj-${a.name}`}
              name={a.title || a.name}
              description={a.description || (a.editable ? '' : t('Written by hand: runs as is, cannot be edited here.'))}
              bodyExtra={
                <div className="flex flex-wrap items-center gap-1">
                  {a.blueprint && (
                    <Chip size="sm" variant="bordered">
                      {titleOf(a.blueprint) ?? a.blueprint}
                    </Chip>
                  )}
                  <Chip size="sm" variant="flat" color={a.status === 'published' ? 'success' : 'warning'}>
                    {t(a.status)}
                  </Chip>
                  <span className="ml-auto text-xs text-default-400">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : ''}</span>
                </div>
              }
              footerItems={
                <CtyButton
                  className="w-full border-1"
                  variant="solid"
                  radius="sm"
                  size="lg"
                  color="primary"
                  isDisabled={!a.editable}
                  onPress={() => navigate(`${basePath}/${a.name}`)}
                  id={`ab-open-${a.name}`}
                >
                  {t('Open')}
                </CtyButton>
              }
              canDelete
              dropdownAriaLabel={t('Journey actions')}
              deleteModalEntityName={a.title || a.name}
              onConfirmDelete={(close) => void remove(a.name, close)}
              isDeleteLoading={deleting === a.name}
              deleteConfirmLabel={t('Delete')}
            />
          </div>
        ))}
      </CardGrid>
      <CreateAsjModal
        isOpen={isCreateOpen}
        onOpenChange={onCreateOpenChange}
        blueprints={blueprints}
        onCreated={(name) => navigate(`${basePath}/${name}`)}
      />
    </section>
  );
}

export function CreateAsjButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation();
  return (
    <CtyButton color="primary" variant="flat" onPress={onPress} id="ab-create-agent" startContent={<Icon icon="lucide:sparkles" width={16} />}>
      {t('Create ASJ')}
    </CtyButton>
  );
}

// The Journeys page: the existing journeys, with the ASJs in the same page. "Create Journey"
// creates an ASJ.
export function JourneysPage() {
  const dialog = useDisclosure();
  const [asjCount, setAsjCount] = useState(0);
  return (
    <AIWorkerList
      category="journeys"
      onCreate={dialog.onOpen}
      hideEmptyState={asjCount > 0}
      beforeList={<AsjJourneys isCreateOpen={dialog.isOpen} onCreateOpenChange={dialog.onOpenChange} onCount={setAsjCount} />}
    />
  );
}

function CreateAsjModal({
  isOpen,
  onOpenChange,
  blueprints,
  onCreated,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  blueprints: Blueprint[];
  onCreated: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [llm, setLlm] = useState(DEFAULT_MODEL);
  const [blueprint, setBlueprint] = useState('');
  const [models, setModels] = useState<string[]>([DEFAULT_MODEL]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setLlm(DEFAULT_MODEL);
    setBlueprint(blueprints.length === 1 ? blueprints[0].id : '');
    agentBuilderApi.llmProfiles().then((p) => p.length && setModels(p), () => undefined);
  }, [isOpen, blueprints]);

  const create = async () => {
    setBusy(true);
    try {
      const wf = await agentBuilderApi.createAgent({ title: title.trim(), llm, blueprint });
      onOpenChange(false);
      onCreated(wf.settings.name);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        <ModalHeader>{t('Create ASJ')}</ModalHeader>
        <ModalBody className="flex flex-col gap-3" data-testid="ab-create-dialog">
          <Input autoFocus label={t('Name')} placeholder={t('e.g. Hypertek email support')} value={title} onValueChange={setTitle} data-testid="ab-create-name" />
          <Select
            label={t('Model')}
            selectedKeys={[llm]}
            onSelectionChange={(keys) => setLlm(String(Array.from(keys)[0] ?? DEFAULT_MODEL))}
            data-testid="ab-create-model"
          >
            {models.map((m) => (
              <SelectItem key={m}>{m === DEFAULT_MODEL ? t('Default model') : m}</SelectItem>
            ))}
          </Select>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-default-600">{t('Blueprint')}</span>
            {blueprints.length === 0 && <p className="text-sm text-warning">{t('No blueprints are available from the agent platform right now.')}</p>}
            {blueprints.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBlueprint(b.id)}
                className={`rounded-lg border p-3 text-left ${blueprint === b.id ? 'border-primary bg-primary/10' : 'border-default-200 hover:bg-default-100'}`}
                data-testid={`ab-blueprint-${b.id}`}
                aria-pressed={blueprint === b.id}
              >
                <div className="font-semibold">{b.title}</div>
                <div className="text-xs text-default-600">{b.description}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(b.triggers).map(([k, v]) => (
                    <Chip key={k} size="sm" variant="flat" color="primary">
                      {v.label || k}
                    </Chip>
                  ))}
                  <Chip size="sm" variant="flat">
                    {t('Starts with {{count}} actions', { count: b.tools.length })}
                  </Chip>
                </div>
              </button>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="flat" onPress={() => onOpenChange(false)}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="primary" onPress={create} isLoading={busy} isDisabled={!title.trim() || !blueprint} id="ab-create-submit">
            {t('Create')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default JourneysPage;
